/**
 * <SeekBar> — l'avancée d'une lecture, qu'on touche ou qu'on glisse pour s'y déplacer (atom).
 *
 * Le curseur des messages vocaux de l'ancien fil (`@react-native-community/slider`, dans
 * `AudioRecordingContainer.js`), sans dépendance de plus : une piste, son remplissage et une
 * pastille, sur le système de répondeurs de React Native (le même sur le web). Comme l'ancien
 * curseur, la lecture ne se déplace qu'au lâcher (`onSlidingComplete`) : pendant le geste, seule la
 * pastille suit le doigt.
 *
 * Contrôlé : `value` (0 à 1) vient du lecteur, `onSeek` y remonte la position choisie. Au lecteur
 * d'écran, c'est un réglage (« adjustable ») qui avance ou recule d'un dixième.
 */
import React, { useRef, useState } from "react"
import { View, type AccessibilityActionEvent, type GestureResponderEvent, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { seekRatio } from "./seekRatio"

export type SeekBarProps = {
    /** Où en est la lecture, de 0 à 1. */
    value: number
    /** La position choisie, de 0 à 1, au lâcher. */
    onSeek: (ratio: number) => void
    /** Le remplissage et la pastille — `primary` par défaut. */
    color?: SemanticColorKey
    /** La piste — `border` par défaut. */
    trackColor?: SemanticColorKey
    /** Ce que dit le lecteur d'écran. */
    accessibilityLabel?: string
    testID?: string
}

/** La cible du doigt : la hauteur du livre, même si la piste n'en dessine que 4 px. */
const TOUCH_HEIGHT = 44
const TRACK_HEIGHT = 4
const THUMB_SIZE = 14
/** Un pas du lecteur d'écran. */
const STEP = 0.1

export function SeekBar(props: SeekBarProps) {
    const { value, onSeek, color = "primary", trackColor = "border", accessibilityLabel, testID } = props

    const width = useRef(0)
    /** Pendant le geste : la position que suit la pastille. */
    const [dragging, setDragging] = useState<number | null>(null)
    const shown = dragging ?? Math.max(0, Math.min(1, value))

    const at = (event: GestureResponderEvent) => seekRatio(event.nativeEvent.locationX, width.current)

    const onAction = (event: AccessibilityActionEvent) => {
        const delta = event.nativeEvent.actionName === "increment" ? STEP : -STEP
        onSeek(Math.max(0, Math.min(1, shown + delta)))
    }

    return (
        <View
            style={styles.touch}
            onLayout={(event: LayoutChangeEvent) => {
                width.current = event.nativeEvent.layout.width
            }}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            // Le fil défile à la verticale : un geste commencé sur la piste reste à la piste.
            onResponderTerminationRequest={() => false}
            onResponderGrant={(event) => setDragging(at(event))}
            onResponderMove={(event) => setDragging(at(event))}
            onResponderRelease={(event) => {
                const ratio = at(event)
                setDragging(null)
                onSeek(ratio)
            }}
            onResponderTerminate={() => setDragging(null)}
            accessible
            accessibilityRole="adjustable"
            accessibilityLabel={accessibilityLabel}
            accessibilityValue={{ min: 0, max: 100, now: Math.round(shown * 100) }}
            accessibilityActions={[{ name: "increment" }, { name: "decrement" }]}
            onAccessibilityAction={onAction}
            testID={testID}
        >
            {/* Le toucher se lit sur la piste entière : ce qu'elle dessine ne le capte pas. */}
            <View style={styles.track(trackColor)} pointerEvents="none">
                <View style={styles.fill(color, shown)} />
            </View>
            <View style={styles.thumb(color, shown)} pointerEvents="none" />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    touch: {
        height: TOUCH_HEIGHT,
        justifyContent: "center",
        width: "100%",
    },
    track: (trackColor: SemanticColorKey) => ({
        height: TRACK_HEIGHT,
        borderRadius: TRACK_HEIGHT / 2,
        backgroundColor: theme.colors[trackColor],
        overflow: "hidden" as const,
    }),
    fill: (color: SemanticColorKey, ratio: number) => ({
        width: `${ratio * 100}%` as `${number}%`,
        height: "100%" as const,
        backgroundColor: theme.colors[color],
    }),
    thumb: (color: SemanticColorKey, ratio: number) => ({
        position: "absolute" as const,
        left: `${ratio * 100}%` as `${number}%`,
        marginLeft: -THUMB_SIZE / 2,
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: THUMB_SIZE / 2,
        backgroundColor: theme.colors[color],
    }),
}))
